import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InputSignal,
  OnInit,
  Signal,
  input,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlContainer, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, distinctUntilChanged, switchMap, of, Observable, map } from 'rxjs';

import { FormHelper } from 'tiime-expert-utils';
import {
  TiimeAutocompleteModule,
  TiimeButtonModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeTooltipModule
} from 'tiime-material';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { UserApiService } from '@api-services/user-api.service';
import { USER_CAPACITIES } from '@constants/user-capacities.constant';
import { UserCapacity } from '@enums/user-capacity.enum';
import { UserForm } from '@forms/user/user-form';
import { PhoneHelper } from '@helpers/phone.helper';
import { remainSubscribedOnError } from '@helpers/rxjs.helper';
import { Civility } from '@models/civility';
import { User } from '@models/user';
import { UserAutocompleteComponent } from '@shared-autocompletes/user-autocomplete/user-autocomplete.component';
import { UserCapacitySelectComponent } from '@shared-selectors/user-capacity-select/user-capacity-select.component';
import { UserCivilitySelectComponent } from '@shared-selectors/user-civility-select/user-civility-select.component';

@Component({
  selector: 'app-create-user-form',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TiimeIconModule,
    TiimeButtonModule,
    TiimeAutocompleteModule,
    TiimeInputContainerModule,
    TiimeTooltipModule,
    UserAutocompleteComponent,
    UserCapacitySelectComponent,
    UserCivilitySelectComponent
  ],
  templateUrl: './create-user-form.component.html',
  styleUrl: './create-user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  host: {
    class: 'tiime-form-grid'
  }
})
export class CreateUserFormComponent implements OnInit, AfterViewInit {
  readonly excludedAutocompleteUsers: InputSignal<User[]> = input<User[]>([]);
  readonly capacities: InputSignal<UserCapacity[]> = input<UserCapacity[]>(USER_CAPACITIES);

  readonly userLastNameInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required<ElementRef<HTMLInputElement>>('userLastNameInput');

  readonly civilities: Signal<Civility[]> = toSignal(this.commonDataApiService.getCivilities(), { rejectErrors: true });

  userForm: UserForm;
  users$: Observable<User[]>;

  constructor(
    private readonly formGroupDirective: FormGroupDirective,
    private readonly userApiService: UserApiService,
    private readonly commonDataApiService: CommonDataApiService
  ) {}

  ngOnInit(): void {
    this.userForm = this.formGroupDirective.control as UserForm;
    this.observeLastNameValueChanges();
  }

  ngAfterViewInit(): void {
    this.focusUserLastNameInput();
  }

  focusUserLastNameInput(): void {
    setTimeout(() => this.userLastNameInput().nativeElement.focus());
  }

  resetUserFormWithUserSuggestion(user: User): void {
    this.userForm.reset(
      {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        civility: user.civility,
        phone: user.phone && PhoneHelper.isValid(user.phone) ? PhoneHelper.format(user.phone) : user.phone,
        email: user.email,
        capacity: this.userForm.capacity.value
      },
      FormHelper.shouldNotEmitEvent
    );
  }

  selectUserSuggestion(user: User): void {
    setTimeout(() => {
      this.resetUserFormWithUserSuggestion(user);

      if (this.userForm.firstName.value && this.userForm.firstName.valid) {
        this.userForm.firstName.disable(FormHelper.shouldNotEmitEvent);
      }
      if (this.userForm.lastName.value && this.userForm.lastName.valid) {
        this.userForm.lastName.disable(FormHelper.shouldNotEmitEvent);
      }
      if (this.userForm.civility.value && this.userForm.civility.valid) {
        this.userForm.civility.disable(FormHelper.shouldNotEmitEvent);
      }
      if (this.userForm.phone.value && this.userForm.phone.valid) {
        this.userForm.phone.disable(FormHelper.shouldNotEmitEvent);
      }
      if (this.userForm.email.value && this.userForm.email.valid) {
        this.userForm.email.disable(FormHelper.shouldNotEmitEvent);
      }
    }, 10);
  }

  deselectUserSuggestion(): void {
    this.userForm.reset({ capacity: this.userForm.capacity.value }, FormHelper.shouldNotEmitEvent);
    this.userForm.enable(FormHelper.shouldNotEmitEvent);
  }

  private observeLastNameValueChanges(): void {
    this.users$ = this.userForm.lastName.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((search: string) => {
        const isValidSearch = !!search && search.length > 1;
        return isValidSearch ? this.userApiService.searchUsers(search) : of<User[]>([]);
      }),
      map((users: User[]) =>
        this.excludedAutocompleteUsers()?.length
          ? users.filter(
              (user: User) =>
                this.excludedAutocompleteUsers().findIndex((excludedUser: User) => excludedUser.id === user.id) <= -1
            )
          : users
      ),
      remainSubscribedOnError()
    );
  }
}
